import { DatePipe } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
    AttributesValueType,
    CompanyAttributeDTO,
    CompanyProductSelectionAttributeWithOverrideInformation
} from '@iota/iota-management-portal-v4-client';
import { ProductAttribute } from 'src/app/shared/constants-and-enums/attribute-types';
import { IotaConfigAttribute, IotaConfigAttributeUnitLabel } from 'src/app/shared/constants-and-enums/iota-config';
import { InhLanguage } from 'src/app/shared/constants-and-enums/language';
import { getIotaConfigAttributeDescription, getIotaConfigAttributeTooltip } from 'src/app/shared/utils';
import { AttributeDetailsDirective, AttributeDetailsInterface } from '../attribute-details.directive';

@Component({
    standalone: false,
    selector: 'app-simplified-attribute',
    templateUrl: './simplified-attribute.component.html'
})
export class SimplifiedAttributeComponent extends AttributeDetailsDirective implements AttributeDetailsInterface, OnInit, OnChanges {
    @Input() iotaConfigAttribute: IotaConfigAttribute;
    @Input() selectedLanguage: InhLanguage;
    @Input() showRealAttributeName = false;

    possibleLocales: string[] = [];

    attributeDescription = undefined;
    attributeTooltip = undefined;
    attributeUnit = undefined;
    attributePossibleValuesWithLabel: { value: string; label?: string }[] = [];

    // Value is important to decide which input to show, all others must be undefined
    stringValue: string;
    locale: string;
    private initialLocale: string;
    uuidValue: string;
    booleanValue: boolean;
    intValue: number;
    numberValue: number;
    dateValue: Date;
    datetimeValue: Date;

    isOverride = false;
    isRequired = false;
    isDeprecated = false;
    canBeDeletedOrModified = true;

    attributeExists = false;
    valueChanged = false;

    newlyCreated = false;

    wouldOverrideParentButCantBeOverridden = false;

    validationError: string | undefined;

    get isInvalid(): boolean {
        return !!this.validationError;
    }

    constructor(datePipe: DatePipe) {
        super(datePipe);
    }

    ngOnInit(): void {
        this.initAttribute(this.currentAttribute);
    }

    // Current attribute changes if the attribute was imported
    ngOnChanges(changes: SimpleChanges): void {
        const prevAttribute = changes?.currentAttribute?.previousValue;
        const currentAttribute = changes?.currentAttribute?.currentValue;

        if (prevAttribute !== currentAttribute) {
            this.initAttribute(this.currentAttribute);
        }
    }

    initAttribute(valueAttribute: CompanyAttributeDTO | CompanyProductSelectionAttributeWithOverrideInformation) {
        this.attributeDescription = getIotaConfigAttributeDescription(this.iotaConfigAttribute, this.selectedLanguage?.countryCode);
        this.attributeTooltip = getIotaConfigAttributeTooltip(this.iotaConfigAttribute, this.selectedLanguage?.countryCode);
        this.attributeUnit = this.iotaConfigAttribute?.customUnit ?? IotaConfigAttributeUnitLabel?.[this.iotaConfigAttribute?.unit];
        this.attributePossibleValuesWithLabel = this.iotaConfigAttribute?.possibleValues?.map((value) => ({
            value,
            label: this.iotaConfigAttribute?.possibleValuesLabels?.[value]?.[this.selectedLanguage?.countryCode]
        }));

        const prevMarkedForUpsert = this.markedForUpsert ?? false;
        const prevMarkedForDelete = this.markedForDelete ?? false;

        const attributeType = this.iotaConfigAttribute?.type;
        this.isRequired = this.iotaConfigAttribute?.required;
        this.isDeprecated = this.iotaConfigAttribute?.deprecated;
        if (this.markIfParentIdIsThisParentId && this.parentId && this.parentId === this.currentAttribute?.parentSkId) {
            this.showEntitySpecificTooltip = true;
            this.isOverride = this.isAttributeWithOverrideInformation(valueAttribute) && valueAttribute.override;
        }

        this.possibleLocales = [...this.locales];
        // If the attribute has a "custom" locale, keep it possible
        if (valueAttribute?.locale && !this.locales.includes(valueAttribute.locale)) {
            this.possibleLocales = [valueAttribute.locale];
        }

        if (this.iotaConfigAttribute.name === 'CUSTOMER_IS_B2C' && this.booleanValue === true) {
            this.canBeDeletedOrModified = false;
        }

        // Set value depending on the type
        switch (attributeType) {
            case 'BOOL': {
                this.booleanValue = valueAttribute?.booleanValue?.[0] ?? false;
                break;
            }
            case 'INTEGER': {
                this.intValue = valueAttribute?.intValue?.[0] ?? 0;
                break;
            }
            case 'NUMBER': {
                this.numberValue = valueAttribute?.numValue?.[0] ?? 0.0;
                break;
            }
            case 'UUID': {
                this.uuidValue = valueAttribute?.uuidValue?.[0] ?? '';
                break;
            }
            case 'DATE': {
                this.dateValue = valueAttribute?.dateValue?.[0] ?? new Date();
                break;
            }
            case 'DATETIME': {
                this.datetimeValue = valueAttribute?.dateTimeValue?.[0] ?? new Date();
                break;
            }
            default: {
                if (this.iotaConfigAttribute?.isArray) {
                    this.stringValue = this.joinValues(valueAttribute?.strValue);
                } else {
                    this.stringValue = valueAttribute?.strValue?.[0] ?? '';
                }
                this.locale = valueAttribute?.locale ?? this.possibleLocales?.[0];
                this.initialLocale = this.locale;
                break;
            }
        }
        this.attributeExists = this.previousAttribute !== undefined;
        this.valueChanged = false;
        this.markedForDelete = false;

        // Imported attributes are marked as
        if (!this.previousAttribute && this.currentAttribute) this.newlyCreated = true;

        // Existing attributes can be checked directly, new or imported attributes not
        if (this.attributeExists) {
            this.checkIfValueHasChangedAndUpdateMark();
        } else {
            this.markedForUpsert = this.shouldBeMarkedForUpsert();
            if (this.markedForUpsert !== prevMarkedForUpsert) {
                this.updateMarkForUpsertValue.emit(this.markedForUpsert ? 1 : -1);
            }
        }

        // Fix delete badge (only needed if attribute was imported and is now marked for upsert)
        if (this.markedForDelete !== prevMarkedForDelete) {
            this.updateMarkForDeleteValue.emit(this.markedForDelete ? 1 : -1);
        }

        //we check this by looking for an existing attribute with a different parent object (product instead of CPS)
        this.wouldOverrideParentButCantBeOverridden =
            this.iotaConfigAttribute.cannotBeOverwritten && this.previousAttribute && this.parentId != this.previousAttribute.parentSkId;

        this.validateAttribute();
    }

    // returns true if the addition of an attribute would override a parent but the attribute must not be overwritten
    changeLocale(event: Event & { target: HTMLSelectElement }) {
        this.locale = event?.target?.value;
        this.checkIfValueHasChangedAndUpdateMark();
    }

    shouldBeMarkedForUpsert() {
        // New attributes are upserted
        if (this.newlyCreated) return true;

        // Changed values are upserted
        if (this.valueChanged) return true;

        // New locales are upserted
        return !!(!this.previousAttribute?.attributeId && this.previousAttribute?.attributeName);
    }

    checkIfValueHasChangedAndUpdateMark() {
        const attributeType = this.iotaConfigAttribute?.type;

        switch (attributeType) {
            case 'BOOL': {
                this.valueChanged = this.booleanValue !== this.previousAttribute?.booleanValue?.[0];
                break;
            }
            case 'INTEGER': {
                this.valueChanged = this.intValue !== this.previousAttribute?.intValue?.[0];
                break;
            }
            case 'NUMBER': {
                this.valueChanged = this.numberValue !== this.previousAttribute?.numValue?.[0];
                break;
            }
            case 'UUID': {
                this.valueChanged = this.uuidValue !== this.previousAttribute?.uuidValue?.[0];
                break;
            }
            case 'DATE': {
                this.valueChanged = this.dateValue !== this.previousAttribute?.dateValue?.[0];
                break;
            }
            case 'DATETIME': {
                this.valueChanged = this.datetimeValue !== this.previousAttribute?.dateTimeValue?.[0];
                break;
            }
            default: {
                let prevValue;
                if (this.iotaConfigAttribute?.isArray) {
                    prevValue = this.joinValues(this.previousAttribute?.strValue);
                } else {
                    prevValue = this.previousAttribute?.strValue?.[0] ?? '';
                }
                this.valueChanged = this.stringValue !== prevValue || this.locale !== this.initialLocale;
                break;
            }
        }

        const prevMarkedForUpsert = this.markedForUpsert ?? false;
        this.markedForUpsert = this.shouldBeMarkedForUpsert();
        if (this.markedForUpsert !== prevMarkedForUpsert) {
            this.updateMarkForUpsertValue.emit(this.markedForUpsert ? 1 : -1);
        }

        this.validateAttribute();
    }

    // Validates the attribute when it is created or actually changed.
    // Add new per-attribute rules here.
    validateAttribute() {
        this.validationError = undefined;

        if (!this.markedForUpsert) return;

        switch (this.iotaConfigAttribute?.name) {
            case ProductAttribute.PRODUCT_AVAILABLE_FROM:
            case ProductAttribute.PRODUCT_DISCONTINUED: {
                if (this.isSelectedDateInThePast()) {
                    this.validationError = $localize`The date cannot be in the past`;
                }
                break;
            }
        }
    }

    private isSelectedDateInThePast() {
        return this.getSelectedDateAtMidnight() < this.getTodayAtMidnight();
    }

    private getSelectedDateAtMidnight(): Date {
        const selectedDate = new Date(this.dateValue ?? this.datetimeValue);
        selectedDate.setHours(0, 0, 0, 0);
        return selectedDate;
    }

    private getTodayAtMidnight(): Date {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    }

    resetValue() {
        this.initAttribute(this.previousAttribute);
    }

    createAttribute() {
        this.newlyCreated = true;
        this.initAttribute(this.currentAttribute);
    }

    markForDelete() {
        // New locale or imported attribute can be deleted from the list
        const newImportedAttribute = !this.previousAttribute && !!this.currentAttribute;
        if ((this.attributeExists || newImportedAttribute) && !this.previousAttribute?.attributeId) {
            this.removeAttribute.emit();
            if (this.markedForUpsert) this.updateMarkForUpsertValue.emit(-1);
            return;
        }

        // New attribute is reset to initial state
        if (!this.attributeExists) {
            this.initAttribute(this.currentAttribute);
            this.newlyCreated = false;
            this.markedForUpsert = false;
            this.updateMarkForUpsertValue.emit(-1);
            return;
        }

        // Existing attribute is marked for delete
        if (this.previousAttribute?.attributeId) {
            this.markedForDelete = true;
            this.updateMarkForDeleteValue.emit(1);
            if (this.markedForUpsert) {
                this.updateMarkForUpsertValue.emit(-1);
            }
        }
    }

    // Copy values from previousAttribute and set new value(s) and locale
    getUpsertedAttribute(): CompanyAttributeDTO | CompanyProductSelectionAttributeWithOverrideInformation {
        if (this.markedForDelete) return undefined;

        if (this.markedForUpsert) {
            const attributeType = this.iotaConfigAttribute?.type;
            const attribute: CompanyAttributeDTO | CompanyProductSelectionAttributeWithOverrideInformation = this.previousAttribute
                ? { ...this.previousAttribute }
                : { parentSkId: this.parentId, attributeName: this.iotaConfigAttribute?.name };
            // If the parentId is set but not the current parentId, then we need to override this attribute, so we remove the audit values and set the parentId
            if (!!attribute.parentSkId && this.parentId !== attribute.parentSkId) {
                this.clearAttributeAuditFieldsAndIds(attribute);
                attribute.parentSkId = this.parentId;
            }

            if (attributeType === 'BOOL')
                return {
                    ...attribute,
                    valueType: AttributesValueType.BOOL,
                    booleanValue: [this.booleanValue]
                };
            if (attributeType === 'INTEGER')
                return {
                    ...attribute,
                    valueType: AttributesValueType.INTEGER,
                    intValue: [this.intValue]
                };
            if (attributeType === 'NUMBER')
                return {
                    ...attribute,
                    valueType: AttributesValueType.NUMBER,
                    numValue: [this.numberValue]
                };
            if (attributeType === 'UUID')
                return {
                    ...attribute,
                    valueType: AttributesValueType.UUID,
                    uuidValue: [this.uuidValue]
                };
            if (attributeType === 'DATE')
                return {
                    ...attribute,
                    valueType: AttributesValueType.DATE,
                    dateValue: [new Date(this.dateValue).toISOString() as any]
                };
            if (attributeType === 'DATETIME')
                return {
                    ...attribute,
                    valueType: AttributesValueType.DATETIME,
                    dateTimeValue: [new Date(this.datetimeValue) as any]
                };

            let stringArray = [this.stringValue];
            if (this.iotaConfigAttribute?.isArray) {
                stringArray = this.parseToStringArray(this.stringValue);
            }
            return { ...attribute, strValue: stringArray, valueType: AttributesValueType.STRING, locale: this.locale };
        }

        return undefined;
    }
}
